import { Component, ElementRef, HostListener, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { PROFILE_TYPE } from '@lib/core/enum';
import { ThemeService } from '@lib/core/service';
import { ShortcutOptions } from '@proget-shared/ui/shortcut';
import { debounceTime, distinctUntilChanged, filter, of, skipWhile, startWith, Subject, Subscription, switchMap, tap } from 'rxjs';

import { SearchResult } from '../../model';
import { SearcherRepository } from '../../searcher.repository';

@Component({
  selector: 'app-searcher-autocomplete',
  templateUrl: './searcher-autocomplete.component.html',
  styleUrls: ['./searcher-autocomplete.component.scss'],
  standalone: false,
})
export class SearcherAutocompleteComponent implements OnInit, OnDestroy {
  public readonly shortcut = input<ShortcutOptions>();
  public readonly shortcutLabel = input<string>('');

  @ViewChild('results', { read: ElementRef })
  public results;

  protected readonly PROFILE_TYPE = PROFILE_TYPE;
  protected readonly QUERY_MIN_LENGTH = 3;

  protected form: FormGroup;
  protected searchResult = new SearchResult();
  protected searchInProgress = false;
  protected focused = false;
  protected searchedPhrase = '';
  protected resultsWidth: number | undefined;

  private readonly resizeSubject = new Subject<boolean>();
  private readonly searchSubscription = new Subscription();

  private positionLeft = 0;

  constructor(
    protected themeService: ThemeService,
    private searcherRepository: SearcherRepository,
    private elementRef: ElementRef,
    formBuilder: FormBuilder,
    router: Router
  ) {
    this.form = formBuilder.group({ query: '' });

    this.searchSubscription.add(
      router.events.pipe(filter((event) => event instanceof NavigationStart))
        .subscribe({
          next: () => {
            this.focused = false;
            this.form.setValue({ query: '' });
          },
        })
    );
  }

  ngOnInit(): void {
    this.searchSubscription.add(
      this.form
        .get('query')
        .valueChanges.pipe(
          debounceTime(1000),
          skipWhile(() => !this.focused),
          distinctUntilChanged(),
          tap({
            next: (queryValue) => {
              this.searchResult = new SearchResult();
              this.searchedPhrase = queryValue;
              this.searchInProgress = true;
            },
          }),
          switchMap((queryValue) => (this.isQueryValid(queryValue)
            ? this.searcherRepository.get(queryValue)
            : of(new SearchResult()))
          )
        )
        .subscribe({
          next: (searchResult) => {
            this.searchResult = searchResult;
            this.searchInProgress = false;

            this.updateResultsWidth();
          },
        })
    );

    this.searchSubscription.add(
      this.resizeSubject.pipe(
        startWith(true)
      )
        .subscribe({
          next: () => {
            const rect = this.elementRef.nativeElement.getBoundingClientRect();

            this.positionLeft = rect.left;
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.resizeSubject.complete();
    this.searchSubscription.unsubscribe();
  }

  @HostListener('document: click', ['$event'])
  public clear(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target)) {
      return;
    }

    this.focused = false;
    this.form.setValue({ query: '' });
  }

  @HostListener('window: resize')
  public onResize(): void {
    this.resizeSubject.next(true);
  }

  public get maxWidth(): number {
    return window.innerWidth - this.positionLeft - 15;
  }

  protected emptyMessageHidden(): boolean {
    return !this.isQueryValid(this.form.value.query) || !this.searchResult.isEmpty
      || this.searchInProgress || this.form.value.query !== this.searchedPhrase;
  }

  protected enterSearchPhraseHidden(): boolean {
    return this.isQueryValid(this.form.value.query) || !this.form.value.query || this.form.value.query !== this.searchedPhrase;
  }

  protected searchResultHidden(): boolean {
    return this.searchResult.isEmpty || this.form.value.query !== this.searchedPhrase;
  }

  private isQueryValid(queryValue: string): boolean {
    return 'string' === typeof queryValue ? queryValue.length >= this.QUERY_MIN_LENGTH : false;
  }

  private updateResultsWidth(): void {
    if (!this.results) {
      return;
    }

    this.resultsWidth = void 0;
    this.results.nativeElement.style.wordWrap = 'normal';
    this.results.nativeElement.style.whiteSpace = 'nowrap';

    setTimeout(() => {
      if (!this.results) {
        return;
      }

      this.resultsWidth = this.results.nativeElement.offsetWidth;
      this.results.nativeElement.style.wordWrap = '';
      this.results.nativeElement.style.whiteSpace = '';
    }, 0);
  }
}
